/**
 * The two SIDES of an agreement — the front-end half of the rule.
 *
 * An Agreement to Purchase / to Rent is the only document CID issues that is a
 * contract BETWEEN two parties, so "the other side" is a question only the OTP and
 * the OTR can answer. `partySides` returns null for everything else, and every
 * screen uses that null to decide whether to offer sequential fill & sign at all —
 * rather than testing `form_type === 'AGREEMENT_LETTER'` in six places, which is
 * the same test written so it can rot separately.
 *
 * Mirrors cid_admin_server/src/utils/bookingFormParties.js and the admin portal
 * src/utils/bookingFormParties.js — same sides, same labels, same rule.
 */

const SIDES_BY_TYPE = {
  'AGREEMENT_LETTER|SALES': { first: 'purchaser', second: 'owner' },
  'AGREEMENT_LETTER|RENT': { first: 'tenant', second: 'landlord' },
};

/** What a side is called on screen. */
export const SIDE_LABEL = {
  purchaser: 'Purchaser',
  owner: 'Vendor / Owner',
  tenant: 'Tenant',
  landlord: 'Landlord',
};

/** All four side names, for validating anything that arrives as a string. */
export const ALL_SIDES = ['purchaser', 'owner', 'tenant', 'landlord'];

/** The two sides of a two-sided document, or null for an appointment letter. */
export function partySides(formType, saleType) {
  return SIDES_BY_TYPE[`${formType}|${saleType}`] || null;
}

/** The side a signatory key belongs to — 'owner_witness' → 'owner'. */
export function sideOfKey(signatoryKey) {
  const key = String(signatoryKey || '');
  return ALL_SIDES.find((side) => key === side || key.startsWith(`${side}_`)) || null;
}

/** The side facing `side` on this document, or null. */
export function otherSide(formType, saleType, side) {
  const sides = partySides(formType, saleType);
  if (!sides) return null;
  if (side === sides.first) return sides.second;
  if (side === sides.second) return sides.first;
  return null;
}

/** The signatory key a fill & sign link is addressed to for a side. */
export function primaryKeyForSide(side) {
  return `${side}_1`;
}

/**
 * The two sides of a form as rows a screen can render, or null.
 * Each row carries the live state of that side, read off the signatories list the
 * server already sends: who it went to, whether they signed, whether the link that
 * is out lets them fill themselves in.
 */
export function fillSignRows(formType, saleType, signatories = []) {
  const sides = partySides(formType, saleType);
  if (!sides) return null;
  return [sides.first, sides.second].map((side) => {
    const key = primaryKeyForSide(side);
    const row = (signatories || []).find((s) => s.key === key) || {};
    return {
      side,
      label: SIDE_LABEL[side] || side,
      signatory_key: key,
      name: row.name || '',
      status: row.status || 'NOT_STARTED',
      sent_to: row.sent_to || row.email || null,
      signed_at: row.signed_at || null,
      can_fill_party: !!row.can_fill_party,
      hide_counterparty: !!row.hide_counterparty,
    };
  });
}
